// src/pages/privacy-policy.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const PolicyWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  color: #0C2340;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  color: #006331;
  margin-top: 30px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  margin-bottom: 30px;
`;

const Button = styled.button`
  background-color: ${props => props.primary ? '#006331' : '#0C2340'};
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  &:hover {
    background-color: ${props => props.primary ? '#00843D' : '#183860'};
  }
`;

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  const handleAccept = () => {
    // Store acceptance in session storage to communicate with Contact page
    sessionStorage.setItem('privacyPolicyAccepted', 'true');
    navigate(-1); // Go back to previous page
  };
  
  const handleGoBack = () => {
    navigate(-1); // Go back to previous page
  };
  
  return (
    <PolicyWrapper>
      <Title>Privacy Policy</Title>
      
      <ButtonContainer>
        <Button primary onClick={handleAccept}>Consent</Button>
        <Button onClick={handleGoBack}>Go Back</Button>
      </ButtonContainer>

      <Section>
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <p>
          Shamrock Services ("we," "our," or "us") is committed to protecting your privacy.
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information
          when you visit our website or submit information through our contact form.
        </p>
      </Section>

      <Section>
        <SectionTitle>Information We Collect</SectionTitle>
        <p>We collect information that you voluntarily provide to us when you:</p>
        <ul>
          <li>Fill out and submit our contact form</li>
          <li>Upload files through our contact form</li>
          <li>Correspond with us by email or other means</li>
        </ul>
        <p>The personal information we collect may include:</p>
        <ul>
          <li>Name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Message content</li>
          <li>Files you upload</li>
          <li>Timestamp of your consent to this policy</li>
        </ul>
      </Section>

      <Section>
        <SectionTitle>How We Use Your Information</SectionTitle>
        <p>We use the information we collect exclusively for the following purposes:</p>
        <ul>
          <li>To respond to your inquiries and contact you about services you have requested</li>
          <li>To provide customer support</li>
          <li>To improve our website and services</li>
          <li>To comply with legal obligations</li>
        </ul>
        <p>
          <strong>We do not sell, trade, or otherwise transfer your personal information to outside parties.</strong>
        </p>
      </Section>

      <Section>
        <SectionTitle>Data Retention</SectionTitle>
        <p>
          We will retain your personal information only for as long as necessary to fulfill the purposes
          outlined in this Privacy Policy. Once we have made contact with you regarding your inquiry,
          we will delete your personal information from our active systems, unless we have a legitimate
          business need to retain it or are required by law to retain it for a longer period.
        </p>
      </Section>

      <Section>
        <SectionTitle>Data Security</SectionTitle>
        <p>
          We implement appropriate technical and organizational measures to protect your personal information.
          All data is stored securely in the United States using industry-standard security practices.
          However, no method of transmission over the Internet or electronic storage is 100% secure,
          so we cannot guarantee absolute security.
        </p>
      </Section>

      <Section>
        <SectionTitle>Your Rights</SectionTitle>
        <p>Depending on your location, you may have certain rights regarding your personal information, including:</p>
        <ul>
          <li>The right to access the personal information we have about you</li>
          <li>The right to request that we correct or update your personal information</li>
          <li>The right to request that we delete your personal information</li>
          <li>The right to object to the processing of your personal information</li>
        </ul>
        <p>
          To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
        </p>
      </Section>

      <Section>
        <SectionTitle>Changes to This Privacy Policy</SectionTitle>
        <p>
          We may update this Privacy Policy from time to time. The updated version will be indicated by an
          updated "Last updated" date and the updated version will be effective as soon as it is accessible.
          We encourage you to review this Privacy Policy frequently to be informed of how we are protecting your information.
        </p>
      </Section>

      <Section>
        <SectionTitle>Contact Us</SectionTitle>
        <p>
          If you have questions or comments about this Privacy Policy, please contact us at:
        </p>
        <p>
          Shamrock Services LLC.<br />
          [Your Address]<br />
          423shamrockservices@gmail.com<br />
          423-660-9125
        </p>
      </Section>

      <Section>
        <SectionTitle>Governing Law</SectionTitle>
        <p>
          This Privacy Policy is governed by and construed in accordance with the laws of the State of Tennessee,
          without giving effect to any principles of conflicts of law.
        </p>
      </Section>
      <ButtonContainer>
        <Button primary onClick={handleAccept}>Consent</Button>
        <Button onClick={handleGoBack}>Go Back</Button>
      </ButtonContainer>
    </PolicyWrapper>
  );
};

export default PrivacyPolicy;